'use client';

import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useState,
} from 'react';

type NavbarContextState = {
  closeModalMenu: () => void;
  goBack?: () => void;
  isFirstLevel: boolean;
  isModalMenuOpen: boolean;
  isSecondLevel: boolean;
  isThirdLevel: boolean;
  modalLevelTitle: null | string;
  openModalMenu?: () => void;
  resetMenuLevel: () => void;
  setModalLevelTitle: (title: string) => void;
  setSecondLevel: () => void;
  setThirdLevel: () => void;
};

type Props = PropsWithChildren<{
  isModalMenuOpenDefault?: true;
}>;

export const NavbarContext = createContext<NavbarContextState>({
  closeModalMenu: () => {
    // Empty function to keep TypeScript happy until the provider renders
  },
  isFirstLevel: true,
  isModalMenuOpen: false,
  isSecondLevel: false,
  isThirdLevel: false,
  modalLevelTitle: null,
  resetMenuLevel: () => {
    // Empty function to keep TypeScript happy until the provider renders
  },
  setModalLevelTitle: () => {
    // Empty function to keep TypeScript happy until the provider renders
  },
  setSecondLevel: () => {
    // Empty function to keep TypeScript happy until the provider renders
  },
  setThirdLevel: () => {
    // Empty function to keep TypeScript happy until the provider renders
  },
});

export const NavbarContextProvider = (props: Props): ReactElement => {
  const { children, isModalMenuOpenDefault = false } = props;

  const [menuLevel, setMenuLevel] = useState<number>(1);
  const [modalLevelTitle, setModalLevelTitle] = useState<string>('');
  const [isModalMenuOpen, setIsModalMenuOpen] = useState<boolean>(
    isModalMenuOpenDefault
  );

  const openModalMenu = () => setIsModalMenuOpen(true);
  const resetMenuLevel = () => setMenuLevel(1);
  const setSecondLevel = () => setMenuLevel(2);
  const setThirdLevel = () => setMenuLevel(3);
  const goBack = () => setMenuLevel(menuLevel - 1);
  const closeModalMenu = () => {
    setIsModalMenuOpen(false);
    setModalLevelTitle('');
    resetMenuLevel();
  };

  return (
    <NavbarContext.Provider
      value={{
        closeModalMenu,
        goBack,
        isFirstLevel: menuLevel === 1,
        isModalMenuOpen,
        isSecondLevel: menuLevel === 2,
        isThirdLevel: menuLevel === 3,
        modalLevelTitle,
        openModalMenu,
        resetMenuLevel,
        setModalLevelTitle,
        setSecondLevel,
        setThirdLevel,
      }}
    >
      {children}
    </NavbarContext.Provider>
  );
};
